import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Check,
  Clock,
  FolderOpen,
  ArrowRight,
  Zap,
  Building2,
} from 'lucide-react';

const ACCENT_STYLE = { background: 'var(--brand-cta-bg)' };

export interface ApplyTrackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrack: () => void;
  onSkip: () => void;
  jobData?: {
    jobTitle?: string;
    companyName?: string;
    jobUrl?: string;
  };
}

export const ApplyTrackModal: React.FC<ApplyTrackModalProps> = ({
  isOpen,
  onClose,
  onTrack,
  onSkip,
  jobData,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleTrack = async () => {
    setIsLoading(true);
    try {
      await onTrack();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 pb-[env(safe-area-inset-bottom)]"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 32, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-[400px] overflow-hidden border border-gray-200 dark:border-gray-700 max-h-[92vh] flex flex-col"
          >
            {/* ── Gradient title bar ── */}
            <div
              className="flex items-center gap-3 px-5 py-4 shrink-0"
              style={ACCENT_STYLE}
            >
              <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shadow-sm shrink-0">
                <Zap className="w-5 h-5 text-white" fill="white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.15em] leading-none mb-1">
                  Resume Optimizer
                </p>
                <h2 className="text-base font-bold text-white leading-tight truncate">
                  {t('optimizer.applyTrackModal.title', 'Resume Downloaded!')}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/15 transition-colors touch-manipulation shrink-0"
                aria-label={t('optimizer.applyTrackModal.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="px-5 pt-4 pb-2 flex-1 overflow-y-auto">
              {/* Success badge */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">
                  {t(
                    'optimizer.applyTrackModal.body',
                    'Would you like to track your job application and manage your job search?'
                  )}
                </p>
              </div>

              {/* Job info card */}
              {jobData?.jobTitle && (
                <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-100 dark:border-gray-700">
                  <div className="w-9 h-9 rounded-lg bg-[#6C47FF]/10 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-[#6C47FF] dark:text-[#8B6FFF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {jobData.jobTitle}
                    </p>
                    {jobData.companyName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {jobData.companyName}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Feature highlights */}
              <div className="space-y-2 mb-5">
                {[
                  { icon: <Check className="w-3 h-3 text-white" strokeWidth={3} />, text: t('optimizer.applyTrackModal.trackStatus', 'Track application status') },
                  { icon: <Clock className="w-3 h-3 text-white" strokeWidth={2.5} />, text: t('optimizer.applyTrackModal.reminders', 'Get reminders for follow-ups') },
                  { icon: <FolderOpen className="w-3 h-3 text-white" strokeWidth={2.5} />, text: t('optimizer.applyTrackModal.organize', 'Organize all your applications') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
                      {item.icon}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-200">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2.5">
                <button
                  onClick={handleTrack}
                  disabled={isLoading}
                  className="w-full min-h-[46px] py-3 px-4 text-white font-semibold rounded-xl transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation text-sm"
                  style={ACCENT_STYLE}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{t('optimizer.applyTrackModal.yesTrack', 'Yes, Track This Application')}</span>
                      <ArrowRight className="w-4 h-4 shrink-0" />
                    </>
                  )}
                </button>

                <button
                  onClick={onSkip}
                  disabled={isLoading}
                  className="w-full min-h-[44px] py-2.5 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-600 dark:text-gray-300 font-medium rounded-xl transition-colors text-sm touch-manipulation"
                >
                  {t('optimizer.applyTrackModal.noThanks', 'No Thanks')}
                </button>
              </div>
            </div>

            {/* ── Footer ── */}
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 shrink-0">
              <p className="text-xs text-center text-gray-400 dark:text-gray-500">
                {t('optimizer.applyTrackModal.privateSecure', 'Your job application data is private and secure')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
